import {
  TD3_ACTIVE_STEPS_MAX,
  TD3_ACTIVE_STEPS_MIN,
  TD3_PLAYABLE_PITCH_MAX,
  TD3_PLAYABLE_PITCH_MIN,
  clampPlayablePitch,
  type Td3Location,
  type Td3Pattern,
  type Td3Step,
} from "./td3Protocol";

export type PresetId =
  | "pure-chaos"
  | "raw-memory-puke"
  | "every-step-panic"
  | "slow-bass-battery"
  | "mid-acid-rot"
  | "high-squeal-rot"
  | "classic-junk"
  | "sparse-corrosion"
  | "slide-storm"
  | "stutter-ties"
  | "micro-loop-twitch"
  | "empty-room-pulse"
  | "pentatonic-junk-funk"
  | "triplet-wobble"
  | "no-slide-machine"
  | "root-anchor"
  | "half-bar-answer"
  | "seven-step-lean"
  | "turnaround-stab"
  | "bridge-dissolve"
  | "palindrome-echo"
  | "euclid-gate"
  | "fibonacci-climb"
  | "prime-step-skip"
  | "binary-counter"
  | "gray-code-bassline"
  | "turing-register-acid"
  | "clave-mutations"
  | "counterpoint-pairs"
  | "sturmian-stepper"
  | "high-low-teeter"
  | "bubble-ladder"
  | "slide-serpent"
  | "morse-burst"
  | "falling-stairs"
  | "octave-pinball"
  | "siren-saw"
  | "ratchet-gaps"
  | "double-back-splice"
  | "wrecking-ball"
  | "custom";

export type ScaleId =
  | "none"
  | "chromatic"
  | "minor-pentatonic"
  | "natural-minor"
  | "dorian"
  | "phrygian"
  | "acid-blues";

export interface ChaosSettings {
  presetId: PresetId;
  lengthMin: number;
  lengthMax: number;
  tripletChance: number;
  accentChance: number;
  slideChance: number;
  tieChance: number;
  restChance: number;
  pitchMin: number;
  pitchMax: number;
  scaleId: ScaleId;
  root: number;
  /** When true, at least one active step is forced to be a real note trigger, not a rest/tie. */
  enforceAudibleStep: boolean;
  allowTieOnFirstStep: boolean;
}

export interface Preset {
  id: Exclude<PresetId, "custom">;
  name: string;
  description: string;
  settings: ChaosSettings;
}

const CHROMATIC = Array.from({ length: 12 }, (_, index) => index);

export const SCALES: Record<ScaleId, number[]> = {
  none: CHROMATIC,
  chromatic: CHROMATIC,
  "minor-pentatonic": [0, 3, 5, 7, 10],
  "natural-minor": [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  "acid-blues": [0, 3, 5, 6, 7, 10],
};

export const ROOT_NAMES = [
  "C",
  "C♯",
  "D",
  "D♯",
  "E",
  "F",
  "F♯",
  "G",
  "G♯",
  "A",
  "A♯",
  "B",
];

export const DEFAULT_CHAOS: ChaosSettings = {
  presetId: "pure-chaos",
  lengthMin: 1,
  lengthMax: 16,
  tripletChance: 0.5,
  accentChance: 0.55,
  slideChance: 0.55,
  tieChance: 0.18,
  restChance: 0.48,
  pitchMin: TD3_PLAYABLE_PITCH_MIN,
  pitchMax: TD3_PLAYABLE_PITCH_MAX,
  scaleId: "none",
  root: 0,
  enforceAudibleStep: true,
  allowTieOnFirstStep: false,
};

export const PRESETS: Preset[] = [
  {
    id: "pure-chaos",
    name: "Pure battery death",
    description:
      "Default. Random length, pitch, triplet, accent, slide, rest and some ties. Blank guard keeps each active pattern from becoming all rests/ties.",
    settings: DEFAULT_CHAOS,
  },
  {
    id: "raw-memory-puke",
    name: "Just Raw mem borked",
    description:
      "The old no-safety-net mode: ties, rests, first-step tie weirdness and totally silent patterns are allowed.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "raw-memory-puke",
      tieChance: 0.5,
      restChance: 0.5,
      enforceAudibleStep: false,
      allowTieOnFirstStep: true,
    },
  },
  {
    id: "every-step-panic",
    name: "Every-step panic",
    description:
      "Fast note-on-every-step chaos. No rests or ties, lots of accent/slide, fixed 16-step length.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "every-step-panic",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.35,
      accentChance: 0.62,
      slideChance: 0.52,
      tieChance: 0,
      restChance: 0,
      pitchMin: 0x18,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "slow-bass-battery",
    name: "Slow bass rot",
    description:
      "Shorter, lower, slower bass accidents with holes, occasional ties and fewer high squeals.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "slow-bass-battery",
      lengthMin: 2,
      lengthMax: 8,
      tripletChance: 0.18,
      accentChance: 0.36,
      slideChance: 0.28,
      tieChance: 0.22,
      restChance: 0.42,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: 0x1b,
      scaleId: "phrygian",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "mid-acid-rot",
    name: "Mid acid rot",
    description:
      "Mid-range 303 junk: still random, but generally lands in a useful acid register.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "mid-acid-rot",
      lengthMin: 5,
      lengthMax: 13,
      tripletChance: 0.28,
      accentChance: 0.44,
      slideChance: 0.46,
      tieChance: 0.08,
      restChance: 0.24,
      pitchMin: 0x18,
      pitchMax: 0x27,
      scaleId: "acid-blues",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "high-squeal-rot",
    name: "High squeal rot",
    description:
      "Upper-register squeals with lots of slide/accent and only light gaps.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "high-squeal-rot",
      lengthMin: 7,
      lengthMax: 16,
      tripletChance: 0.42,
      accentChance: 0.72,
      slideChance: 0.76,
      tieChance: 0.06,
      restChance: 0.16,
      pitchMin: 0x24,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "classic-junk",
    name: "Classic junk acid",
    description:
      "Less feral: bluesy notes, sane timing, enough slide/accent to sound like corrupted acid rather than silence.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "classic-junk",
      lengthMin: 8,
      lengthMax: 16,
      tripletChance: 0.18,
      accentChance: 0.38,
      slideChance: 0.42,
      tieChance: 0.04,
      restChance: 0.18,
      pitchMin: 0x12,
      pitchMax: 0x2b,
      scaleId: "acid-blues",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "sparse-corrosion",
    name: "Sparse corrosion",
    description:
      "Broken gaps, short phrases and occasional nasty spikes, protected from writing fully blank active ranges.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "sparse-corrosion",
      lengthMin: 2,
      lengthMax: 12,
      restChance: 0.72,
      tieChance: 0.12,
      accentChance: 0.45,
      slideChance: 0.2,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "slide-storm",
    name: "Slide storm",
    description:
      "Aggressive acid smear: high slide/accent density with very few ties, because slide does the musical glide job.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "slide-storm",
      lengthMin: 6,
      lengthMax: 16,
      restChance: 0.12,
      tieChance: 0.03,
      accentChance: 0.75,
      slideChance: 0.9,
      pitchMin: 0x12,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "stutter-ties",
    name: "Stutter tie rot",
    description:
      "A special tie-heavy recipe for authentic sequencer weirdness. Still guarded so one real note trigger survives.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "stutter-ties",
      lengthMin: 8,
      lengthMax: 16,
      tripletChance: 0.62,
      restChance: 0.18,
      tieChance: 0.55,
      accentChance: 0.52,
      slideChance: 0.58,
      pitchMin: 0x18,
      pitchMax: 0x2a,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "micro-loop-twitch",
    name: "Micro loop twitch",
    description:
      "Tiny 1-4 step fragments with hot accents, very few rests and almost no ties. Built for obsessive short-loop jolts.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "micro-loop-twitch",
      lengthMin: 1,
      lengthMax: 4,
      tripletChance: 0.24,
      accentChance: 0.82,
      slideChance: 0.18,
      tieChance: 0.02,
      restChance: 0.08,
      pitchMin: 0x18,
      pitchMax: 0x24,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "empty-room-pulse",
    name: "Empty room pulse",
    description:
      "Long patterns with mostly empty timing, low movement and the blank guard keeping a single pulse alive.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "empty-room-pulse",
      lengthMin: 12,
      lengthMax: 16,
      tripletChance: 0.08,
      accentChance: 0.18,
      slideChance: 0.08,
      tieChance: 0.04,
      restChance: 0.86,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: 0x20,
      scaleId: "natural-minor",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "pentatonic-junk-funk",
    name: "Pentatonic junk funk",
    description:
      "A-minor pentatonic guardrails, syncopated gaps and medium accents for broken but more musical bass movement.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "pentatonic-junk-funk",
      lengthMin: 8,
      lengthMax: 16,
      tripletChance: 0.16,
      accentChance: 0.58,
      slideChance: 0.32,
      tieChance: 0.06,
      restChance: 0.28,
      pitchMin: 0x12,
      pitchMax: 0x2a,
      scaleId: "minor-pentatonic",
      root: 9,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "triplet-wobble",
    name: "Triplet wobble",
    description:
      "Triplet-heavy mid-low patterns with moderate slide and gaps, tuned for lopsided rolling phrases.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "triplet-wobble",
      lengthMin: 6,
      lengthMax: 12,
      tripletChance: 0.88,
      accentChance: 0.48,
      slideChance: 0.48,
      tieChance: 0.1,
      restChance: 0.22,
      pitchMin: 0x0f,
      pitchMax: 0x24,
      scaleId: "dorian",
      root: 2,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "no-slide-machine",
    name: "No-slide machine",
    description:
      "Fixed 16-step chromatic punching with no slide, no tie and strong accents for a rigid mechanical contrast.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "no-slide-machine",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.05,
      accentChance: 0.85,
      slideChance: 0,
      tieChance: 0,
      restChance: 0.05,
      pitchMin: 0x18,
      pitchMax: 0x27,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "root-anchor",
    name: "Root anchor",
    description:
      "A stable low-register 16-step chain anchor with sparse rests, low slide and steady accents.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "root-anchor",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.04,
      accentChance: 0.38,
      slideChance: 0.12,
      tieChance: 0.02,
      restChance: 0.08,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: 0x1b,
      scaleId: "acid-blues",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "half-bar-answer",
    name: "Half-bar answer",
    description:
      "An exact 8-step response phrase with mid-register motion, more gaps and stronger accents.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "half-bar-answer",
      lengthMin: 8,
      lengthMax: 8,
      tripletChance: 0.12,
      accentChance: 0.62,
      slideChance: 0.32,
      tieChance: 0.04,
      restChance: 0.28,
      pitchMin: 0x18,
      pitchMax: 0x27,
      scaleId: "dorian",
      root: 7,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "seven-step-lean",
    name: "Seven-step lean",
    description:
      "A 7-step odd-cycle phrase that leans against 16-step material when chained.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "seven-step-lean",
      lengthMin: 7,
      lengthMax: 7,
      tripletChance: 0.18,
      accentChance: 0.52,
      slideChance: 0.42,
      tieChance: 0.06,
      restChance: 0.14,
      pitchMin: 0x12,
      pitchMax: 0x24,
      scaleId: "phrygian",
      root: 2,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "turnaround-stab",
    name: "Turnaround stab",
    description:
      "A short 2-4 step punctuation pattern with high accents and upper-register stabs.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "turnaround-stab",
      lengthMin: 2,
      lengthMax: 4,
      tripletChance: 0.1,
      accentChance: 0.9,
      slideChance: 0.22,
      tieChance: 0,
      restChance: 0.03,
      pitchMin: 0x24,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "bridge-dissolve",
    name: "Bridge dissolve",
    description:
      "A 12-15 step transition recipe with loose gaps, ties, slides and a higher triplet chance.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "bridge-dissolve",
      lengthMin: 12,
      lengthMax: 15,
      tripletChance: 0.54,
      accentChance: 0.42,
      slideChance: 0.48,
      tieChance: 0.24,
      restChance: 0.56,
      pitchMin: 0x12,
      pitchMax: 0x2b,
      scaleId: "natural-minor",
      root: 5,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "palindrome-echo",
    name: "Palindrome echo",
    description:
      "Chain-aware mirrored pairs: every second generated pattern is the previous pattern reversed.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "palindrome-echo",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.16,
      accentChance: 0.48,
      slideChance: 0.38,
      tieChance: 0.04,
      restChance: 0.2,
      pitchMin: 0x12,
      pitchMax: 0x2a,
      scaleId: "acid-blues",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "euclid-gate",
    name: "Euclid gate",
    description:
      "A 5-in-16 Euclidean gate structure with rotated pulses across chained patterns.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "euclid-gate",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.2,
      accentChance: 0.58,
      slideChance: 0.28,
      tieChance: 0,
      restChance: 0.68,
      pitchMin: 0x12,
      pitchMax: 0x27,
      scaleId: "minor-pentatonic",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "fibonacci-climb",
    name: "Fibonacci climb",
    description:
      "A 13-step pitch contour driven by Fibonacci offsets, with accents on Fibonacci-numbered steps.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "fibonacci-climb",
      lengthMin: 13,
      lengthMax: 13,
      tripletChance: 0.13,
      accentChance: 0.34,
      slideChance: 0.34,
      tieChance: 0,
      restChance: 0.08,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: 0x2a,
      scaleId: "natural-minor",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "prime-step-skip",
    name: "Prime step skip",
    description:
      "Only prime-numbered steps fire in a 16-step bar, creating a sparse mathematical rhythm.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "prime-step-skip",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.11,
      accentChance: 0.7,
      slideChance: 0.18,
      tieChance: 0,
      restChance: 0.62,
      pitchMin: 0x12,
      pitchMax: 0x2a,
      scaleId: "phrygian",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "binary-counter",
    name: "Binary counter",
    description:
      "Chain-aware 4-bit gates expanded across 16 steps, so each slot advances the on/off pattern.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "binary-counter",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.04,
      accentChance: 0.62,
      slideChance: 0.24,
      tieChance: 0,
      restChance: 0.5,
      pitchMin: 0x18,
      pitchMax: 0x2b,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "gray-code-bassline",
    name: "Gray code bassline",
    description:
      "Eight-slot Gray-code mutations: one structural bit changes between adjacent slots, while groups and banks shift root, contour and grit.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "gray-code-bassline",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.08,
      accentChance: 0.56,
      slideChance: 0.38,
      tieChance: 0,
      restChance: 0.32,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: 0x2a,
      scaleId: "acid-blues",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "turing-register-acid",
    name: "Turing register acid",
    description:
      "A seeded 16-bit shift register per group-bank advances across slots, turning repeatable bit streams into gates, accents, slides and note jumps.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "turing-register-acid",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.18,
      accentChance: 0.58,
      slideChance: 0.48,
      tieChance: 0.08,
      restChance: 0.34,
      pitchMin: 0x12,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      scaleId: "acid-blues",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "clave-mutations",
    name: "Clave mutations",
    description:
      "Clave, tresillo and cinquillo cells rotate, swap accents and pick up a few acid mutations across each eight-slot bank.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "clave-mutations",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.06,
      accentChance: 0.66,
      slideChance: 0.26,
      tieChance: 0,
      restChance: 0.6,
      pitchMin: 0x0f,
      pitchMax: 0x2a,
      scaleId: "minor-pentatonic",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "counterpoint-pairs",
    name: "Counterpoint pairs",
    description:
      "Adjacent slot pairs form call-and-answer phrases: odd slots state a motif, even slots answer with displacement, inversion and accent swaps.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "counterpoint-pairs",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.12,
      accentChance: 0.52,
      slideChance: 0.34,
      tieChance: 0.02,
      restChance: 0.28,
      pitchMin: 0x12,
      pitchMax: 0x2b,
      scaleId: "dorian",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "sturmian-stepper",
    name: "Sturmian stepper",
    description:
      "Golden-ratio gate spacing makes balanced uneven phrases; slots rotate density, register and accent gravity without becoming straight Euclid.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "sturmian-stepper",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.14,
      accentChance: 0.54,
      slideChance: 0.32,
      tieChance: 0,
      restChance: 0.48,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: 0x2b,
      scaleId: "natural-minor",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "high-low-teeter",
    name: "High-low teeter",
    description:
      "A hard alternating low/high pattern with slides on the upward lurches and accents on the high hits.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "high-low-teeter",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.08,
      accentChance: 0.7,
      slideChance: 0.5,
      tieChance: 0,
      restChance: 0,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "bubble-ladder",
    name: "Bubble ladder",
    description:
      "Three-note rising bubbles with a breath at the end of each cluster, climbing across the bar.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "bubble-ladder",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.18,
      accentChance: 0.44,
      slideChance: 0.54,
      tieChance: 0,
      restChance: 0.25,
      pitchMin: 0x0f,
      pitchMax: 0x2a,
      scaleId: "dorian",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "slide-serpent",
    name: "Slide serpent",
    description:
      "A snaking slide path whose pitch centre rises across Bank A and falls across Bank B.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "slide-serpent",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.22,
      accentChance: 0.5,
      slideChance: 0.95,
      tieChance: 0,
      restChance: 0.08,
      pitchMin: 0x12,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "morse-burst",
    name: "Morse burst",
    description:
      "Fixed-pitch Morse words with quote-seeded slot phrases and varied active lengths.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "morse-burst",
      lengthMin: 3,
      lengthMax: 16,
      tripletChance: 0.05,
      accentChance: 0.64,
      slideChance: 0.18,
      tieChance: 0.2,
      restChance: 0.42,
      pitchMin: 0x18,
      pitchMax: 0x2a,
      scaleId: "minor-pentatonic",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "falling-stairs",
    name: "Falling stairs",
    description:
      "A descending staircase that drops through the range and pauses hard on each landing.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "falling-stairs",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.1,
      accentChance: 0.52,
      slideChance: 0.16,
      tieChance: 0,
      restChance: 0.18,
      pitchMin: 0x0f,
      pitchMax: 0x2b,
      scaleId: "natural-minor",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "octave-pinball",
    name: "Octave pinball",
    description:
      "Same-note rebounds across octaves, with accented ricochets and little gaps between bounces.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "octave-pinball",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.12,
      accentChance: 0.68,
      slideChance: 0.2,
      tieChance: 0,
      restChance: 0.14,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      scaleId: "acid-blues",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "siren-saw",
    name: "Siren saw",
    description:
      "A 12-step sliding saw wave that snaps low again after peaking high.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "siren-saw",
      lengthMin: 12,
      lengthMax: 12,
      tripletChance: 0.42,
      accentChance: 0.58,
      slideChance: 1,
      tieChance: 0,
      restChance: 0,
      pitchMin: 0x12,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "ratchet-gaps",
    name: "Ratchet gaps",
    description:
      "Burst groups that shrink and open into rests, useful for chopped chain fills.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "ratchet-gaps",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.34,
      accentChance: 0.72,
      slideChance: 0.18,
      tieChance: 0,
      restChance: 0.5,
      pitchMin: 0x18,
      pitchMax: 0x2b,
      scaleId: "chromatic",
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "double-back-splice",
    name: "Double-back splice",
    description:
      "First half plays forward, second half doubles back in swapped pairs for a cut-up answer.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "double-back-splice",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.15,
      accentChance: 0.5,
      slideChance: 0.36,
      tieChance: 0.02,
      restChance: 0.2,
      pitchMin: 0x12,
      pitchMax: 0x2a,
      scaleId: "acid-blues",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
  {
    id: "wrecking-ball",
    name: "Wrecking ball",
    description:
      "Low-weighted thuds with sudden high accented smashes every four steps.",
    settings: {
      ...DEFAULT_CHAOS,
      presetId: "wrecking-ball",
      lengthMin: 16,
      lengthMax: 16,
      tripletChance: 0.06,
      accentChance: 0.82,
      slideChance: 0.38,
      tieChance: 0,
      restChance: 0.18,
      pitchMin: TD3_PLAYABLE_PITCH_MIN,
      pitchMax: TD3_PLAYABLE_PITCH_MAX,
      scaleId: "phrygian",
      root: 0,
      enforceAudibleStep: true,
      allowTieOnFirstStep: false,
    },
  },
];

export interface GeneratedPattern {
  pattern: Td3Pattern;
  seed: string;
}

export interface GeneratePatternOptions {
  locations?: Td3Location[];
}

interface RecipeContext {
  patternIndex: number;
  patternCount: number;
  /** Absolute TD-3 memory index, 0-63. Stable across full, range, bank and single-slot writes. */
  locationIndex: number;
  /** Group/bank bucket, 0-7: I-A, I-B, II-A, II-B, etc. */
  groupBankIndex: number;
  /** Pattern Group I-IV, stored as 0-3. */
  groupIndex: number;
  /** Pattern slot 1-8, stored as 0-7. */
  slotIndex: number;
  /** Pattern Bank A/B, stored as 0/1. */
  bankIndex: 0 | 1;
  /** Slot pair bucket: 1-2, 3-4, 5-6, 7-8. Not a TD-3 track number. */
  slotPairIndex: 0 | 1 | 2 | 3;
  pairIndex: 0 | 1;
  /** Pattern number within the group: 1A-8A then 1B-8B, stored as 0-15. */
  patternNumber: number;
  /** Absolute occurrence of this slot-pair bucket across Group I-IV and Bank A/B. */
  slotPairOccurrenceIndex: number;
  slotPairOccurrenceCount: number;
}

interface RecipeVariation {
  pitchOffset: number;
  widePitchOffset: number;
  rhythmOffset: number;
  phraseOffset: number;
  accentOffset: number;
  slideOffset: number;
  restOffset: number;
  highJumpOffset: number;
  bankSalt: number;
  bankPhase: number;
  invert: boolean;
  triplet: boolean;
}

function xmur3(seed: string): () => number {
  let hash = 1779033703 ^ seed.length;
  for (let index = 0; index < seed.length; index += 1) {
    hash = Math.imul(hash ^ seed.charCodeAt(index), 3432918353);
    hash = (hash << 13) | (hash >>> 19);
  }
  return function nextHash() {
    hash = Math.imul(hash ^ (hash >>> 16), 2246822507);
    hash = Math.imul(hash ^ (hash >>> 13), 3266489909);
    return (hash ^= hash >>> 16) >>> 0;
  };
}

export function mulberry32(seed: number): () => number {
  return function random() {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function rngFromSeed(seed: string): () => number {
  const seedFactory = xmur3(seed);
  return mulberry32(seedFactory());
}

export function makeEntropySeed(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `td3-${crypto.randomUUID()}`;
  }
  const fallback = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString(
    36,
  );
  return `td3-${Date.now().toString(36)}-${fallback}`;
}

function clampInt(
  value: number,
  min: number,
  max: number,
  fallback = min,
): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, Math.round(numeric)));
}

function clampProbability(value: number): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(1, numeric));
}

function isScaleId(value: unknown): value is ScaleId {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(SCALES, value)
  );
}

function isPresetId(value: unknown): value is PresetId {
  return value === "custom" || PRESETS.some((preset) => preset.id === value);
}

export function normaliseSettings(settings: ChaosSettings): ChaosSettings {
  const lengthMin = clampInt(
    settings.lengthMin,
    TD3_ACTIVE_STEPS_MIN,
    TD3_ACTIVE_STEPS_MAX,
    TD3_ACTIVE_STEPS_MIN,
  );
  const lengthMax = clampInt(
    settings.lengthMax,
    TD3_ACTIVE_STEPS_MIN,
    TD3_ACTIVE_STEPS_MAX,
    TD3_ACTIVE_STEPS_MAX,
  );
  const pitchMin = clampPlayablePitch(Number(settings.pitchMin));
  const pitchMax = clampPlayablePitch(Number(settings.pitchMax));

  return {
    ...settings,
    presetId: isPresetId(settings.presetId) ? settings.presetId : "custom",
    lengthMin: Math.min(lengthMin, lengthMax),
    lengthMax: Math.max(lengthMin, lengthMax),
    tripletChance: clampProbability(settings.tripletChance),
    accentChance: clampProbability(settings.accentChance),
    slideChance: clampProbability(settings.slideChance),
    tieChance: clampProbability(settings.tieChance),
    restChance: clampProbability(settings.restChance),
    pitchMin: Math.min(pitchMin, pitchMax),
    pitchMax: Math.max(pitchMin, pitchMax),
    scaleId: isScaleId(settings.scaleId) ? settings.scaleId : "none",
    root: clampInt(settings.root, 0, 11, 0),
    enforceAudibleStep: Boolean(settings.enforceAudibleStep),
    allowTieOnFirstStep: Boolean(settings.allowTieOnFirstStep),
  };
}

function randomInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

function chance(rng: () => number, probability: number): boolean {
  return rng() < probability;
}

function pitchCandidates(settings: ChaosSettings): number[] {
  if (settings.scaleId === "none") {
    return [];
  }
  const scale = SCALES[settings.scaleId] ?? CHROMATIC;
  const candidates: number[] = [];
  for (let pitch = settings.pitchMin; pitch <= settings.pitchMax; pitch += 1) {
    const relative = ((pitch % 12) - settings.root + 12) % 12;
    if (scale.includes(relative)) {
      candidates.push(pitch);
    }
  }
  return candidates;
}

function randomPitch(
  rng: () => number,
  settings: ChaosSettings,
  candidates: number[],
): number {
  if (candidates.length > 0) {
    return candidates[randomInt(rng, 0, candidates.length - 1)];
  }
  return randomInt(rng, settings.pitchMin, settings.pitchMax);
}

function isNoteTrigger(step: Td3Step): boolean {
  return !step.rest && !step.tie;
}

function forceAtLeastOneNoteTrigger(
  steps: Td3Step[],
  activeSteps: number,
  rng: () => number,
): void {
  if (steps.slice(0, activeSteps).some(isNoteTrigger)) return;
  const stepIndex = randomInt(rng, 0, activeSteps - 1);
  steps[stepIndex].rest = false;
  steps[stepIndex].tie = false;
}

function cloneStep(step: Td3Step): Td3Step {
  return { ...step };
}

function clonePattern(pattern: Td3Pattern): Td3Pattern {
  return {
    ...pattern,
    steps: pattern.steps.map(cloneStep),
  };
}

function candidatePitchAt(
  settings: ChaosSettings,
  candidates: number[],
  index: number,
): number {
  if (candidates.length > 0) {
    const wrapped =
      ((index % candidates.length) + candidates.length) % candidates.length;
    return candidates[wrapped];
  }
  const span = settings.pitchMax - settings.pitchMin + 1;
  const wrapped = ((index % span) + span) % span;
  return clampPlayablePitch(settings.pitchMin + wrapped);
}

function candidatePitchAtClamped(
  settings: ChaosSettings,
  candidates: number[],
  index: number,
): number {
  if (candidates.length > 0) {
    return candidates[clampInt(index, 0, candidates.length - 1, 0)];
  }
  const offset = clampInt(index, 0, settings.pitchMax - settings.pitchMin, 0);
  return clampPlayablePitch(settings.pitchMin + offset);
}

function clampCandidatePitch(
  settings: ChaosSettings,
  candidates: number[],
  value: number,
): number {
  if (candidates.length === 0) {
    return clampPlayablePitch(value);
  }
  let best = candidates[0];
  let bestDistance = Math.abs(best - value);
  for (const candidate of candidates) {
    const distance = Math.abs(candidate - value);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

function pitchSpan(
  settings: ChaosSettings,
  candidates: number[],
): number {
  return Math.max(
    1,
    candidates.length > 0
      ? candidates.length - 1
      : settings.pitchMax - settings.pitchMin,
  );
}

function pitchByRatio(
  settings: ChaosSettings,
  candidates: number[],
  ratio: number,
  offset = 0,
): number {
  const safeRatio = Math.max(0, Math.min(1, ratio));
  return candidatePitchAt(
    settings,
    candidates,
    Math.round(safeRatio * pitchSpan(settings, candidates)) + offset,
  );
}

function positiveModulo(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus;
}

function slotPairFromSlot(slotIndex: number): 0 | 1 | 2 | 3 {
  return Math.min(3, Math.floor(Math.max(0, slotIndex) / 2)) as
    | 0
    | 1
    | 2
    | 3;
}

function buildRecipeContexts(
  count: number,
  options?: GeneratePatternOptions,
): RecipeContext[] {
  return Array.from({ length: count }, (_, patternIndex) => {
    const location = options?.locations?.[patternIndex];
    const groupIndex =
      location?.group ?? Math.floor((patternIndex % 64) / 16);
    const patternNumber = location
      ? location.slot + (location.bank === "B" ? 8 : 0)
      : patternIndex % 16;
    const slotIndex = location?.slot ?? patternNumber % 8;
    const bankIndex: 0 | 1 =
      location?.bank === "B" || (!location && patternNumber >= 8) ? 1 : 0;
    const slotPairIndex = slotPairFromSlot(slotIndex);
    const pairIndex = (slotIndex % 2) as 0 | 1;
    const locationIndex = groupIndex * 16 + patternNumber;
    const groupBankIndex = groupIndex * 2 + bankIndex;

    return {
      patternIndex,
      patternCount: count,
      locationIndex,
      groupBankIndex,
      groupIndex,
      slotIndex,
      bankIndex,
      slotPairIndex,
      pairIndex,
      patternNumber,
      slotPairOccurrenceIndex: groupBankIndex,
      slotPairOccurrenceCount: 8,
    };
  });
}

function buildRecipeVariation(
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  seed: string,
): RecipeVariation {
  const variationRng = rngFromSeed(
    `${seed}:shape:${settings.presetId}:${context.locationIndex}`,
  );
  const bankRng = rngFromSeed(
    `${seed}:shape-bank:${settings.presetId}:${context.groupBankIndex}`,
  );
  const narrowPitchSpread = Math.max(
    1,
    Math.min(5, Math.round(pitchSpan(settings, candidates) * 0.18)),
  );
  const widePitchSpread = Math.max(
    2,
    Math.min(9, Math.round(pitchSpan(settings, candidates) * 0.34)),
  );

  return {
    pitchOffset: randomInt(variationRng, -narrowPitchSpread, narrowPitchSpread),
    widePitchOffset: randomInt(
      variationRng,
      -widePitchSpread,
      widePitchSpread,
    ),
    rhythmOffset: randomInt(variationRng, 0, TD3_ACTIVE_STEPS_MAX - 1),
    phraseOffset: randomInt(variationRng, 0, 15),
    accentOffset: randomInt(variationRng, 0, 7),
    slideOffset: randomInt(variationRng, 0, 7),
    restOffset: randomInt(variationRng, 0, 7),
    highJumpOffset: randomInt(variationRng, -4, 5),
    bankSalt: randomInt(bankRng, 1, 0xffff),
    bankPhase: randomInt(bankRng, 0, TD3_ACTIVE_STEPS_MAX - 1),
    invert: chance(variationRng, 0.5),
    triplet: chance(variationRng, settings.tripletChance),
  };
}

function reversePattern(source: Td3Pattern): Td3Pattern {
  const reversed = clonePattern(source);
  for (let index = 0; index < source.activeSteps; index += 1) {
    reversed.steps[index] = cloneStep(
      source.steps[source.activeSteps - 1 - index],
    );
  }
  reversed.steps[0].tie = false;
  return reversed;
}

function mirrorActiveSteps(pattern: Td3Pattern): Td3Pattern {
  const mirrored = clonePattern(pattern);
  const half = Math.ceil(pattern.activeSteps / 2);
  for (let index = half; index < pattern.activeSteps; index += 1) {
    mirrored.steps[index] = cloneStep(
      mirrored.steps[pattern.activeSteps - 1 - index],
    );
  }
  mirrored.steps[0].tie = false;
  return mirrored;
}

function euclideanPulse(
  stepIndex: number,
  activeSteps: number,
  pulses: number,
  rotation: number,
): boolean {
  const rotated = (stepIndex + rotation) % activeSteps;
  return (
    Math.floor(((rotated + 1) * pulses) / activeSteps) !==
    Math.floor((rotated * pulses) / activeSteps)
  );
}

function applyEuclideanGate(pattern: Td3Pattern, patternIndex = 0): void {
  let pulseCount = 0;
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const pulse = euclideanPulse(index, pattern.activeSteps, 5, patternIndex);
    const step = pattern.steps[index];
    step.rest = !pulse;
    step.tie = false;
    step.accent = pulse && pulseCount % 2 === 0;
    step.slide = pulse && pulseCount % 3 === 2;
    if (pulse) pulseCount += 1;
  }
}

const FIBONACCI_OFFSETS = [0, 1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144];
const FIBONACCI_STEP_NUMBERS = new Set([1, 2, 3, 5, 8, 13]);

function applyFibonacciClimb(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const stepNumber = index + 1;
    const offset = FIBONACCI_OFFSETS[index % FIBONACCI_OFFSETS.length];
    const contourOffset = variation.invert ? -offset : offset;
    const step = pattern.steps[index];
    step.pitch = candidatePitchAt(
      settings,
      candidates,
      contourOffset + context.locationIndex + variation.pitchOffset,
    );
    step.rest =
      (stepNumber + variation.restOffset) % 5 === 0 &&
      stepNumber !== pattern.activeSteps;
    step.tie = false;
    step.accent =
      FIBONACCI_STEP_NUMBERS.has(stepNumber) ||
      ((stepNumber + variation.accentOffset) % 8 === 0 && !step.rest);
    step.slide =
      positiveModulo(contourOffset + variation.slideOffset, 2) === 1 &&
      !step.rest;
  }
}

function isPrimeStep(stepNumber: number): boolean {
  if (stepNumber < 2) return false;
  for (let divisor = 2; divisor * divisor <= stepNumber; divisor += 1) {
    if (stepNumber % divisor === 0) return false;
  }
  return true;
}

function applyPrimeStepSkip(pattern: Td3Pattern): void {
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const prime = isPrimeStep(index + 1);
    const step = pattern.steps[index];
    step.rest = !prime;
    step.tie = false;
    step.accent = prime;
    step.slide = prime && (index + 1) % 4 === 3;
  }
}

function applyBinaryCounter(pattern: Td3Pattern, patternIndex = 0): void {
  const counter = ((patternIndex + 1) & 0x0f) || 0x0f;
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const bitIndex = index % 4;
    const bitOn = (counter & (1 << bitIndex)) !== 0;
    const step = pattern.steps[index];
    step.rest = !bitOn;
    step.tie = false;
    step.accent = bitOn && bitIndex === 0;
    step.slide = bitOn && (counter & (1 << ((bitIndex + 1) % 4))) !== 0;
  }
}

function grayCode(value: number): number {
  return value ^ (value >> 1);
}

const GRAY_CODE_CONTOUR = [
  0, 2, 4, 7, 4, 2, 5, 3, 0, 2, 6, 8, 7, 5, 3, 1,
] as const;

function applyGrayCodeBassline(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const code = grayCode(context.slotIndex) & 0x07;
  const syncopate = (code & 0x01) !== 0;
  const invertContour = (code & 0x02) !== 0;
  const liftSecondHalf = (code & 0x04) !== 0;
  const baseOffset =
    context.groupIndex * 2 +
    context.bankIndex * 4 +
    (variation.bankSalt % 5) +
    variation.pitchOffset;
  const accentPhase =
    context.groupBankIndex + context.slotIndex + variation.accentOffset;

  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const phraseIndex = (index + variation.bankPhase) % GRAY_CODE_CONTOUR.length;
    const rawContour = GRAY_CODE_CONTOUR[phraseIndex];
    const contour = invertContour ? 8 - rawContour : rawContour;
    const mutation =
      (syncopate && index % 4 === 2 ? 2 : 0) +
      (liftSecondHalf && index >= 8 ? 3 : 0);
    const anchored = index % 4 === 0;
    const offbeat =
      (phraseIndex + context.groupIndex + (syncopate ? 1 : 0)) % 4 === 2;
    const fill =
      (phraseIndex + context.bankIndex + (liftSecondHalf ? 2 : 0)) % 7 === 3;
    const inversionGate =
      invertContour &&
      (phraseIndex + context.slotIndex + variation.restOffset) % 6 === 1;
    const active = anchored || offbeat || fill || inversionGate;
    const step = pattern.steps[index];

    step.pitch = candidatePitchAt(
      settings,
      candidates,
      baseOffset + contour + mutation,
    );
    step.rest = !active;
    step.tie = false;
    step.accent = active && (anchored || (index + accentPhase) % 6 === 0);
    step.slide =
      active &&
      index < pattern.activeSteps - 1 &&
      ((invertContour && phraseIndex % 4 === 2) ||
        (liftSecondHalf && phraseIndex % 5 === 0));
  }
}

function advanceTuringRegister(value: number): number {
  const feedback =
    ((value >> 0) ^ (value >> 2) ^ (value >> 3) ^ (value >> 5)) & 0x01;
  return ((value >> 1) | (feedback << 15)) & 0xffff;
}

function advanceRegister(value: number, count: number): number {
  let next = value & 0xffff;
  for (let index = 0; index < count; index += 1) {
    next = advanceTuringRegister(next || 0xace1);
  }
  return next || 0xace1;
}

function applyTuringRegisterAcid(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  let register = advanceRegister(
    variation.bankSalt ^
      ((context.groupIndex + 1) * 0x1234) ^
      (context.bankIndex * 0x5678),
    context.slotIndex * (5 + context.bankIndex) + (variation.phraseOffset % 5),
  );
  const baseOffset =
    context.groupBankIndex * 2 +
    context.slotPairIndex +
    variation.pitchOffset;

  for (let index = 0; index < pattern.activeSteps; index += 1) {
    register = advanceTuringRegister(register);
    const gateBit = register & 0x01;
    const syncBit = (register >> 3) & 0x01;
    const jumpBit = (register >> 7) & 0x01;
    const accentBit = (register >> 11) & 0x01;
    const slideBit = (register >> 13) & 0x01;
    const trigger =
      index % 4 === 0 ||
      gateBit === 1 ||
      (syncBit === 1 && (index + context.slotIndex) % 3 === 0);
    const tie =
      trigger &&
      index > 0 &&
      index % 4 !== 0 &&
      gateBit === 1 &&
      syncBit === 1 &&
      (index + context.groupIndex) % 5 === 0;
    const step = pattern.steps[index];

    step.pitch = candidatePitchAt(
      settings,
      candidates,
      baseOffset + ((register >> 4) & 0x07) + (jumpBit ? 7 : 0),
    );
    step.rest = !trigger;
    step.tie = tie;
    step.accent = trigger && !tie && (accentBit === 1 || index % 4 === 0);
    step.slide = trigger && !tie && (slideBit === 1 || jumpBit === 1);
  }
}

const CLAVE_CELLS: ReadonlyArray<ReadonlyArray<number>> = [
  [0, 3, 6, 10, 12],
  [0, 4, 6, 9, 12],
  [0, 3, 7, 10, 13],
  [0, 2, 4, 7, 10, 12],
  [0, 3, 5, 8, 11, 14],
  [0, 2, 5, 7, 10, 13],
  [0, 4, 7, 11, 14],
  [0, 3, 6, 8, 10, 13],
] as const;

function rotatedCell(cell: ReadonlyArray<number>, rotation: number): Set<number> {
  return new Set(
    cell.map((step) =>
      positiveModulo(step + rotation, TD3_ACTIVE_STEPS_MAX),
    ),
  );
}

function applyClaveMutations(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const cell =
    CLAVE_CELLS[
      positiveModulo(
        context.groupBankIndex +
          context.slotPairIndex +
          (variation.bankSalt % CLAVE_CELLS.length),
        CLAVE_CELLS.length,
      )
    ];
  const rotation =
    context.slotIndex * 2 +
    context.groupIndex +
    context.bankIndex +
    variation.bankPhase;
  const activeSteps = rotatedCell(cell, rotation);
  const extraPulse = positiveModulo(
    rotation + 5 + context.slotIndex + variation.restOffset,
    TD3_ACTIVE_STEPS_MAX,
  );
  const dropPulse = positiveModulo(
    rotation + 10 + context.slotPairIndex + variation.phraseOffset,
    TD3_ACTIVE_STEPS_MAX,
  );

  if (context.slotIndex >= 4) activeSteps.add(extraPulse);
  if (context.bankIndex === 1 && activeSteps.size > 5) activeSteps.delete(dropPulse);

  let noteIndex = 0;
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const active = activeSteps.has(index);
    const step = pattern.steps[index];
    step.pitch = candidatePitchAt(
      settings,
      candidates,
      context.groupBankIndex * 2 +
        context.slotPairIndex +
        noteIndex +
        variation.pitchOffset,
    );
    step.rest = !active;
    step.tie = false;
    step.accent =
      active &&
      (index % 4 === 0 ||
        (index + context.slotIndex + variation.accentOffset) % 5 === 0);
    step.slide =
      active &&
      index < pattern.activeSteps - 1 &&
      ((index + context.bankIndex + variation.slideOffset) % 4 === 1 ||
        activeSteps.has((index + 1) % pattern.activeSteps));
    if (active) noteIndex += context.bankIndex === 0 ? 1 : 2;
  }
}

const COUNTERPOINT_CONTOUR = [
  0, 2, 5, 3, 7, 5, 2, 4, 0, 3, 8, 5, 10, 7, 5, 2,
] as const;

interface CounterpointEvent {
  active: boolean;
  pitchIndex: number;
  accent: boolean;
  slide: boolean;
}

function counterpointSourceEvent(
  stepIndex: number,
  context: RecipeContext,
  variation: RecipeVariation,
): CounterpointEvent {
  const phase =
    variation.bankPhase +
    context.slotPairIndex * 3 +
    context.groupIndex +
    context.bankIndex;
  const position = (stepIndex + phase) % TD3_ACTIVE_STEPS_MAX;
  const active =
    position % 4 === 0 ||
    (position + context.slotPairIndex) % 5 === 2 ||
    (position + context.bankIndex) % 7 === 3;
  return {
    active,
    pitchIndex:
      context.groupBankIndex * 2 +
      context.slotPairIndex * 3 +
      COUNTERPOINT_CONTOUR[position] +
      (variation.bankSalt % 5),
    accent: active && (position % 4 === 0 || position % 7 === 3),
    slide: active && (position + context.slotPairIndex) % 5 === 2,
  };
}

function applyCounterpointPairs(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const center = Math.round(pitchSpan(settings, candidates) / 2);
  const displacement = 3 + context.groupIndex + context.bankIndex;

  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const sourceIndex =
      context.pairIndex === 0
        ? index
        : positiveModulo(index + displacement, pattern.activeSteps);
    const source = counterpointSourceEvent(sourceIndex, context, variation);
    const pitchIndex =
      context.pairIndex === 0
        ? source.pitchIndex + variation.pitchOffset
        : center * 2 - source.pitchIndex + variation.widePitchOffset;
    const step = pattern.steps[index];

    step.pitch = candidatePitchAt(settings, candidates, pitchIndex);
    step.rest = !source.active;
    step.tie = false;
    step.accent =
      context.pairIndex === 0
        ? source.accent
        : source.active && !source.accent;
    step.slide =
      source.active &&
      index < pattern.activeSteps - 1 &&
      (context.pairIndex === 0
        ? source.slide
        : !source.slide || (index + variation.slideOffset) % 6 === 0);
  }
}

function sturmianPulse(
  stepIndex: number,
  density: number,
  phase: number,
): boolean {
  const slope = (Math.sqrt(5) - 1) / 2;
  const left = Math.floor((stepIndex + phase) * density * slope);
  const right = Math.floor((stepIndex + 1 + phase) * density * slope);
  return left !== right;
}

function applySturmianStepper(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const density =
    0.58 +
    context.slotPairIndex * 0.08 +
    context.bankIndex * 0.1 +
    (variation.bankSalt % 5) * 0.015;
  const phase =
    context.slotIndex +
    context.groupIndex * 2 +
    context.bankIndex * 5 +
    variation.bankPhase;
  const baseOffset =
    context.groupBankIndex * 2 +
    context.slotIndex +
    variation.pitchOffset;
  let pulseCount = 0;

  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const pulse = sturmianPulse(index, density, phase);
    const contourIndex = Math.floor(
      positiveModulo(
        Math.floor((index + phase) * ((Math.sqrt(5) + 1) / 2)),
        12,
      ),
    );
    const step = pattern.steps[index];

    step.pitch = candidatePitchAt(
      settings,
      candidates,
      baseOffset + contourIndex + Math.floor(pulseCount / 2),
    );
    step.rest = !pulse;
    step.tie = false;
    step.accent =
      pulse &&
      (pulseCount === 0 ||
        (index + context.slotPairIndex + variation.accentOffset) % 5 === 0);
    step.slide =
      pulse &&
      index < pattern.activeSteps - 1 &&
      (contourIndex % 4 === context.bankIndex ||
        (index + variation.slideOffset) % 7 === 0);
    if (pulse) pulseCount += 1;
  }
}

function applyHighLowTeeter(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const baseOffset =
    context.slotPairIndex * 3 +
    context.groupIndex +
    context.bankIndex * 5 +
    context.slotPairOccurrenceIndex +
    variation.pitchOffset;
  const phase =
    context.slotPairIndex +
    context.pairIndex +
    context.bankIndex +
    context.slotPairOccurrenceIndex +
    variation.rhythmOffset;
  const low = candidatePitchAt(settings, candidates, baseOffset);
  const high = clampCandidatePitch(
    settings,
    candidates,
    low +
      7 +
      context.slotPairIndex * 2 +
      context.bankIndex * 5 +
      variation.highJumpOffset,
  );
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const highStep = (index + phase) % 2 === 1;
    const step = pattern.steps[index];
    step.pitch = highStep ? high : low;
    step.rest = false;
    step.tie = false;
    step.accent =
      highStep ||
      (index + context.slotPairIndex + variation.accentOffset) % 8 === 0;
    step.slide =
      !highStep ||
      (context.bankIndex === 1 &&
        (index + variation.slideOffset) % 4 === 2);
  }
}

function applyBubbleLadder(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const breathPosition = variation.rhythmOffset % 4;
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const group = Math.floor(index / 4);
    const position = index % 4;
    const activePosition =
      position > breathPosition ? position - 1 : position;
    const step = pattern.steps[index];
    step.pitch = candidatePitchAt(
      settings,
      candidates,
      context.locationIndex +
        variation.pitchOffset +
        group * 2 +
        activePosition,
    );
    step.rest = position === breathPosition;
    step.tie = false;
    step.accent = !step.rest && activePosition === variation.accentOffset % 3;
    step.slide =
      !step.rest &&
      (activePosition === positiveModulo(1 + variation.slideOffset, 3) ||
        activePosition === positiveModulo(2 + variation.slideOffset, 3));
  }
}

function applySlideSerpent(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const maxPitchIndex =
    candidates.length > 0
      ? Math.max(0, candidates.length - 1)
      : Math.max(0, settings.pitchMax - settings.pitchMin);
  const slotRatio = context.slotIndex / 7;
  const bankArcRatio = context.bankIndex === 0 ? slotRatio : 1 - slotRatio;
  const chainRatio =
    context.groupIndex % 2 === 0 ? bankArcRatio : 1 - bankArcRatio;
  const chainCenter = Math.round(chainRatio * maxPitchIndex) + variation.pitchOffset;
  const snakeWidth = Math.max(
    1,
    Math.round(Math.max(1, maxPitchIndex) * (variation.invert ? 0.22 : 0.14)),
  );
  const phase =
    context.patternNumber +
    context.groupIndex +
    context.slotPairOccurrenceIndex +
    variation.phraseOffset;
  const reverse =
    (context.bankIndex + context.pairIndex + context.groupIndex) % 2 === 1;
  const restModulo =
    5 + ((context.slotPairIndex + context.pairIndex + variation.restOffset) % 3);
  const accentPeak =
    positiveModulo(
      Math.round(chainRatio * Math.max(1, pattern.activeSteps - 1)) +
        variation.accentOffset,
      pattern.activeSteps,
    );
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const position = index / Math.max(1, pattern.activeSteps - 1);
    const bend = Math.sin(
      (position * 2 + phase / pattern.activeSteps) * Math.PI,
    );
    const tilt = (position - 0.5) * snakeWidth;
    const localOffset = Math.round(bend * snakeWidth + tilt);
    const pitchIndex = chainCenter + (reverse ? -localOffset : localOffset);
    const step = pattern.steps[index];
    step.pitch = candidatePitchAtClamped(settings, candidates, pitchIndex);
    step.rest =
      (index +
        context.slotPairOccurrenceIndex +
        context.bankIndex +
        variation.rhythmOffset) %
        restModulo ===
      restModulo - 1;
    step.tie = false;
    step.accent = index === 0 || index === accentPeak;
    step.slide = !step.rest && index < pattern.activeSteps - 1;
  }
}

type MorseToken = "N" | "T" | "R";

const MORSE_CODE: Record<string, string> = {
  A: ".-",
  B: "-...",
  C: "-.-.",
  D: "-..",
  E: ".",
  F: "..-.",
  G: "--.",
  H: "....",
  I: "..",
  J: ".---",
  K: "-.-",
  L: ".-..",
  M: "--",
  N: "-.",
  O: "---",
  P: ".--.",
  Q: "--.-",
  R: ".-.",
  S: "...",
  T: "-",
  U: "..-",
  V: "...-",
  W: ".--",
  X: "-..-",
  Y: "-.--",
  Z: "--..",
  "0": "-----",
  "1": ".----",
  "2": "..---",
  "3": "...--",
  "4": "....-",
  "5": ".....",
  "6": "-....",
  "7": "--...",
  "8": "---..",
  "9": "----.",
};

const MORSE_BURST_PHRASES: ReadonlyArray<ReadonlyArray<ReadonlyArray<string>>> = [
  [
    ["IS", "IT", "GO", "NO"],
    ["AI", "ID", "WE", "ME"],
    ["US", "AS", "BE", "WE"],
    ["I AM", "I", "AM", "ME"],
    ["SUN", "EYE", "SEE", "UP"],
    ["DEN", "IN", "ON", "NO"],
    ["GO", "ON", "IS", "IT"],
    ["TAO", "DO", "TO", "NO"],
  ],
  [
    ["DO", "AI", "ID", "BE"],
    ["EWE", "WE", "ME", "US"],
    ["SEE", "EYE", "IS", "IT"],
    ["IF", "I AM", "AM", "I"],
    ["SUN", "DEN", "UP", "EYE"],
    ["AS", "IF", "IN", "NO"],
    ["ON", "GO", "TO", "BE"],
    ["TAO", "NO", "DO", "TO"],
  ],
] as const;

function morseTokensFromMessage(message: string): MorseToken[] {
  const tokens: MorseToken[] = [];
  const pushGap = (gapLength: number) => {
    while (
      tokens.length > 0 &&
      tokens[tokens.length - 1] === "R" &&
      gapLength > 0
    ) {
      gapLength -= 1;
    }
    for (let index = 0; index < gapLength; index += 1) {
      tokens.push("R");
    }
  };
  for (const character of message.toUpperCase()) {
    if (character === " ") {
      pushGap(2);
      continue;
    }
    const code = MORSE_CODE[character];
    if (!code) continue;
    if (tokens.length > 0) pushGap(1);
    for (let index = 0; index < code.length; index += 1) {
      if (index > 0) pushGap(1);
      tokens.push("N");
      if (code[index] === "-") tokens.push("T");
    }
  }
  const fallbackTokens: MorseToken[] = ["N", "R"];
  return (tokens.length > 0 ? tokens : fallbackTokens).slice(
    0,
    TD3_ACTIVE_STEPS_MAX,
  );
}

function applyMorseBurst(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const slotPhrases = MORSE_BURST_PHRASES[context.bankIndex][context.slotIndex];
  const message =
    slotPhrases[
      (context.groupIndex + variation.phraseOffset) % slotPhrases.length
    ];
  const tokens = morseTokensFromMessage(message);
  const pulsePitch = clampCandidatePitch(
    settings,
    candidates,
    0x18 +
      variation.pitchOffset +
      context.slotIndex +
      context.bankIndex * 5 +
      context.groupIndex * 2,
  );
  pattern.activeSteps = clampInt(
    tokens.length,
    TD3_ACTIVE_STEPS_MIN,
    TD3_ACTIVE_STEPS_MAX,
    TD3_ACTIVE_STEPS_MAX,
  );
  pattern.triplet = false;
  for (let index = 0; index < TD3_ACTIVE_STEPS_MAX; index += 1) {
    const token = tokens[index];
    const step = pattern.steps[index];
    step.pitch = pulsePitch;
    step.rest = token === undefined || token === "R";
    step.tie = token === "T";
    step.accent =
      token === "N" &&
      (index +
        context.slotIndex +
        context.bankIndex +
        context.groupIndex +
        variation.accentOffset) %
        4 !==
        0;
    step.slide = false;
  }
}

function applyFallingStairs(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const highestIndex = pitchSpan(settings, candidates);
  const riseInstead =
    ((context.bankIndex + context.slotPairIndex + context.groupIndex) % 3 ===
      1) !== variation.invert;
  const pitchOffset =
    context.slotPairOccurrenceIndex +
    context.pairIndex * 2 +
    variation.widePitchOffset;
  const landing =
    (3 + context.slotPairIndex + context.bankIndex + variation.restOffset) % 4;
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const step = pattern.steps[index];
    const stairIndex = riseInstead
      ? index + pitchOffset
      : highestIndex - index - pitchOffset;
    step.pitch = candidatePitchAt(settings, candidates, stairIndex);
    step.rest =
      (index + context.pairIndex + variation.rhythmOffset) % 4 === landing;
    step.tie = false;
    step.accent =
      (index + context.slotPairIndex + variation.accentOffset) % 4 === 0;
    step.slide =
      (index + context.bankIndex + variation.slideOffset) % 4 === 1;
  }
}

function applyOctavePinball(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const lowNotes = [0x0c, 0x0f, 0x11, 0x13, 0x16, 0x13, 0x11, 0x0f];
  const phase =
    context.slotPairOccurrenceIndex +
    context.slotPairIndex * 2 +
    context.bankIndex * 4 +
    variation.phraseOffset;
  const baseJump = context.bankIndex === 1 || context.pairIndex === 1 ? 24 : 12;
  const jump = Math.max(7, baseJump + variation.highJumpOffset);
  const restPosition =
    (7 + context.slotPairIndex + context.pairIndex + variation.restOffset) % 8;
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const low = lowNotes[(index + phase) % lowNotes.length];
    const high = clampCandidatePitch(
      settings,
      candidates,
      low + (index % 4 === 1 ? jump : 12),
    );
    const step = pattern.steps[index];
    step.pitch =
      index % 2 === 0
        ? clampCandidatePitch(
            settings,
            candidates,
            low + context.slotPairIndex + variation.pitchOffset,
          )
        : high;
    step.rest =
      (index + context.bankIndex + variation.rhythmOffset) % 8 ===
      restPosition;
    step.tie = false;
    step.accent = (index + variation.accentOffset) % 2 === 1;
    step.slide = (index + context.pairIndex + variation.slideOffset) % 4 === 2;
  }
}

function applySirenSaw(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const phase =
    (context.slotPairOccurrenceIndex +
      context.slotPairIndex * 3 +
      context.pairIndex +
      variation.phraseOffset) %
    pattern.activeSteps;
  const reverse =
    ((context.bankIndex + context.slotPairIndex + context.groupIndex) % 2 ===
      1) !== variation.invert;
  const snapPoint =
    (pattern.activeSteps -
      1 +
      context.pairIndex +
      context.slotPairIndex +
      variation.accentOffset) %
    pattern.activeSteps;
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const sawIndex = (index + phase) % pattern.activeSteps;
    const ratio = reverse
      ? 1 - sawIndex / Math.max(1, pattern.activeSteps - 1)
      : sawIndex / Math.max(1, pattern.activeSteps - 1);
    const step = pattern.steps[index];
    step.pitch = pitchByRatio(
      settings,
      candidates,
      ratio,
      context.slotPairIndex + context.bankIndex + variation.pitchOffset,
    );
    step.rest = false;
    step.tie = false;
    step.accent = index === snapPoint;
    step.slide = true;
  }
}

const RATCHET_GAP_MASK = [
  true,
  true,
  true,
  false,
  true,
  true,
  false,
  false,
  true,
  true,
  true,
  false,
  true,
  false,
  false,
  false,
] as const;

function applyRatchetGaps(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const maskIndex =
      (index + context.locationIndex + variation.rhythmOffset) %
      RATCHET_GAP_MASK.length;
    const previousMaskIndex =
      (maskIndex - 1 + RATCHET_GAP_MASK.length) % RATCHET_GAP_MASK.length;
    const active = RATCHET_GAP_MASK[maskIndex];
    const step = pattern.steps[index];
    step.pitch = candidatePitchAt(
      settings,
      candidates,
      Math.floor(index / 2) + variation.pitchOffset,
    );
    step.rest = !active;
    step.tie = false;
    step.accent =
      active &&
      (index === 0 ||
        !RATCHET_GAP_MASK[previousMaskIndex] ||
        (index + variation.accentOffset) % 7 === 0);
    step.slide = active && (index + variation.slideOffset) % 3 === 1;
  }
}

function applyDoubleBackSplice(pattern: Td3Pattern): void {
  const secondHalfSource = [6, 7, 4, 5, 2, 3, 0, 1];
  for (let offset = 0; offset < 8; offset += 1) {
    pattern.steps[8 + offset] = cloneStep(pattern.steps[secondHalfSource[offset]]);
    pattern.steps[8 + offset].tie = false;
  }
}

function applyWreckingBall(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  context: RecipeContext,
  variation: RecipeVariation,
): void {
  const smashPosition =
    (3 +
      context.slotPairIndex +
      context.bankIndex +
      context.slotPairOccurrenceIndex +
      variation.rhythmOffset) %
    4;
  const restPosition =
    (2 + context.pairIndex + context.groupIndex + variation.restOffset) % 4;
  const pitchOffset =
    context.slotPairIndex * 2 +
    context.bankIndex * 3 +
    context.slotPairOccurrenceIndex +
    variation.pitchOffset;
  for (let index = 0; index < pattern.activeSteps; index += 1) {
    const position = index % 4;
    const smash = position === smashPosition;
    const step = pattern.steps[index];
    step.pitch = smash
      ? settings.pitchMax
      : candidatePitchAt(settings, candidates, pitchOffset + position);
    step.rest = position === restPosition && !smash;
    step.tie = false;
    step.accent =
      smash ||
      position === (context.slotPairIndex + variation.accentOffset) % 4;
    step.slide =
      position === (1 + context.bankIndex + variation.slideOffset) % 4 ||
      smash;
  }
}

function shapePattern(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  candidates: number[],
  patternIndex?: number,
  previous?: Td3Pattern,
  recipeContext?: RecipeContext,
  recipeVariation?: RecipeVariation,
): Td3Pattern {
  const context = recipeContext ?? buildRecipeContexts(1)[0];
  const variation =
    recipeVariation ??
    buildRecipeVariation(settings, candidates, context, "standalone-shape");
  switch (settings.presetId) {
    case "palindrome-echo":
      if (previous) return reversePattern(previous);
      return patternIndex === undefined ? mirrorActiveSteps(pattern) : pattern;
    case "euclid-gate":
      applyEuclideanGate(pattern, patternIndex ?? 0);
      break;
    case "fibonacci-climb":
      applyFibonacciClimb(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "prime-step-skip":
      applyPrimeStepSkip(pattern);
      break;
    case "binary-counter":
      applyBinaryCounter(pattern, patternIndex ?? 0);
      break;
    case "gray-code-bassline":
      applyGrayCodeBassline(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "turing-register-acid":
      applyTuringRegisterAcid(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "clave-mutations":
      applyClaveMutations(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "counterpoint-pairs":
      applyCounterpointPairs(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "sturmian-stepper":
      applySturmianStepper(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "high-low-teeter":
      applyHighLowTeeter(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "bubble-ladder":
      applyBubbleLadder(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "slide-serpent":
      applySlideSerpent(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "morse-burst":
      applyMorseBurst(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "falling-stairs":
      applyFallingStairs(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "octave-pinball":
      applyOctavePinball(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "siren-saw":
      applySirenSaw(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "ratchet-gaps":
      applyRatchetGaps(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    case "double-back-splice":
      applyDoubleBackSplice(pattern);
      break;
    case "wrecking-ball":
      applyWreckingBall(pattern, settings, candidates, context, variation);
      pattern.triplet = variation.triplet;
      break;
    default:
      break;
  }
  return pattern;
}

function generateBasePattern(
  settings: ChaosSettings,
  rng: () => number,
  candidates: number[],
): Td3Pattern {
  const activeSteps = randomInt(rng, settings.lengthMin, settings.lengthMax);

  const steps: Td3Step[] = Array.from(
    { length: TD3_ACTIVE_STEPS_MAX },
    (_, index) => ({
      pitch: randomPitch(rng, settings, candidates),
      accent: chance(rng, settings.accentChance),
      slide: chance(rng, settings.slideChance),
      tie:
        (settings.allowTieOnFirstStep || index > 0) &&
        chance(rng, settings.tieChance),
      rest: chance(rng, settings.restChance),
    }),
  );

  return {
    activeSteps,
    triplet: chance(rng, settings.tripletChance),
    steps,
  };
}

function finalisePattern(
  pattern: Td3Pattern,
  settings: ChaosSettings,
  rng: () => number,
): Td3Pattern {
  if (settings.enforceAudibleStep) {
    forceAtLeastOneNoteTrigger(pattern.steps, pattern.activeSteps, rng);
  }
  return pattern;
}

export function generatePattern(
  settingsInput: ChaosSettings,
  seedOrRng?: string | (() => number),
): GeneratedPattern {
  const settings = normaliseSettings(settingsInput);
  const seed = typeof seedOrRng === "string" ? seedOrRng : makeEntropySeed();
  const rng = typeof seedOrRng === "function" ? seedOrRng : rngFromSeed(seed);
  const candidates = pitchCandidates(settings);
  const context = buildRecipeContexts(1)[0];
  const variation = buildRecipeVariation(settings, candidates, context, seed);
  const pattern = finalisePattern(
    shapePattern(
      generateBasePattern(settings, rng, candidates),
      settings,
      candidates,
      undefined,
      undefined,
      context,
      variation,
    ),
    settings,
    rng,
  );

  return {
    seed,
    pattern,
  };
}

export function generatePatterns(
  settings: ChaosSettings,
  count: number,
  seed: string,
  options?: GeneratePatternOptions,
): Td3Pattern[] {
  const safeSettings = normaliseSettings(settings);
  const safeCount = clampInt(count, 0, 4096, 0);
  const rng = rngFromSeed(seed);
  const candidates = pitchCandidates(safeSettings);
  const contexts = buildRecipeContexts(safeCount, options);
  const patterns: Td3Pattern[] = [];
  for (let index = 0; index < safeCount; index += 1) {
    const variation = buildRecipeVariation(
      safeSettings,
      candidates,
      contexts[index],
      seed,
    );
    const shaped = shapePattern(
      generateBasePattern(safeSettings, rng, candidates),
      safeSettings,
      candidates,
      index,
      index % 2 === 1 ? patterns[index - 1] : undefined,
      contexts[index],
      variation,
    );
    patterns.push(finalisePattern(shaped, safeSettings, rng));
  }
  return patterns;
}

export function applyPreset(presetId: PresetId): ChaosSettings {
  const preset = PRESETS.find((item) => item.id === presetId);
  return normaliseSettings(
    preset ? { ...preset.settings } : { ...DEFAULT_CHAOS, presetId: "custom" },
  );
}
